import { computed, onBeforeUnmount, onMounted, ref, toValue, unref, watch } from 'vue';
import { useQuery, useQueryClient } from '@tanstack/vue-query';

import { useEventHub } from '@satellite/composables/useEventHub';
import yardTasksService from '@satellite/services/yard-tasks-service';

import { transformTaskApiResponseToViewData } from '../helpers/yardTaskUtils';
import { yardQueryKeys } from './yard/yardQueryKeys';

import type { ComputedRef, Ref } from 'vue';
import type { Maybe, TaskApiResponseData, TaskViewData } from '@nova/core';

export interface UseYardTaskFetcherOptions {
  /**
   * Task ID to fetch. Can be a ref for reactivity.
   * Optional if task (with id) is provided.
   */
  taskId?: Ref<string | null | undefined>;

  /**
   * Optional task object. Used as placeholderData while the query loads/refetches.
   * Effective id is derived from taskId or task.id (explicit taskId wins; mismatch triggers a warning).
   */
  task?: Ref<Maybe<TaskViewData>> | ComputedRef<Maybe<TaskViewData>> | TaskViewData;
  /**
   * If true, the task will be refreshed automatically when the task is updated (via eventHub).
   * Default: true
   */
  live?: boolean;
  /**
   * Stale time in milliseconds.
   * Default: Infinity (never stale, until a Subspace event invalidates the query; manual refresh is always available)
   */
  staleTime?: number;
  /**
   * Callback invoked whenever the task ref is updated with a non-null value.
   */
  onUpdateReceived?: (task: TaskViewData) => void;
}

export interface YardTaskUpdateEvent {
  id: string | null;
  type?: string;
  child?: {
    assetContainerId?: string;
    originSpotId?: string;
    destinationSpotId?: string;
  };
}

export interface YardTaskFetcherDependencies {
  warehouseTimezone?: string | Ref<string | undefined> | (() => string | undefined);
}

export function useYardTaskFetcher(
  options: UseYardTaskFetcherOptions = {},
  dependencies?: YardTaskFetcherDependencies
) {
  const {
    taskId,
    task: taskOption,
    live = true,
    staleTime = Infinity,
    onUpdateReceived
  } = options || {};
  const { warehouseTimezone: providedTimezone } = dependencies || {};

  const eventHub = useEventHub();
  const rawTask = ref<TaskApiResponseData | null>(null);
  const queryClient = useQueryClient();

  const isInViewport = ref(false);
  const pendingInvalidation = ref(false);

  const warehouseTimezone = computed(() => {
    return toValue(providedTimezone) || 'utc';
  });

  const effectiveTaskId = computed(() => {
    const explicitTaskId = unref(taskId);

    if (explicitTaskId) {
      /**
       * Warn if both are provided but don't match.
       */
      const initialTaskId = unref(taskOption)?.id;

      if (initialTaskId && initialTaskId !== explicitTaskId) {
        console.warn(
          '[useYardTaskFetcher] taskId mismatch:',
          `explicit taskId (${explicitTaskId}) does not match initialTask.id (${initialTaskId}). Using explicit taskId.`
        );
      }

      return explicitTaskId;
    }

    return unref(taskOption)?.id || null;
  });

  const taskPlaceholder = computed(() => toValue(taskOption) ?? undefined);

  const taskQuery = useQuery<TaskViewData | null, Error, TaskViewData | null>({
    queryKey: computed(() =>
      effectiveTaskId.value ? yardQueryKeys.task(effectiveTaskId.value) : []
    ),
    queryFn: async ({ signal }) => {
      if (!effectiveTaskId.value) {
        return null;
      }

      const response = (await yardTasksService.getYardTask(effectiveTaskId.value, {
        requestConfig: {
          signal
        }
      })) as TaskApiResponseData;
      rawTask.value = response;

      return transformTaskApiResponseToViewData(response);
    },
    enabled: computed(() => Boolean(effectiveTaskId.value) && isInViewport.value),
    placeholderData: taskPlaceholder,
    staleTime
  });

  const task = computed<TaskViewData | null>(() => taskQuery.data.value || null);

  const isLoading = computed(() => taskQuery.isLoading.value);
  const isFetching = computed(() => taskQuery.isFetching.value);
  const error = computed<Error | null>(() => (taskQuery.error.value as Error | null) ?? null);

  const invalidate = () => {
    if (effectiveTaskId.value) {
      queryClient.invalidateQueries({
        queryKey: yardQueryKeys.task(effectiveTaskId.value)
      });
    }
  };

  const refresh = async () => {
    await taskQuery.refetch();
  };

  const invalidateTasksQueries = (event: YardTaskUpdateEvent) => {
    if (event?.child?.assetContainerId) {
      queryClient.invalidateQueries({
        queryKey: yardQueryKeys.assetContainer(event.child.assetContainerId)
      });
    }
    if (event?.child?.originSpotId) {
      queryClient.invalidateQueries({
        queryKey: yardQueryKeys.spotDetail(event.child.originSpotId)
      });
    }
    if (event?.child?.destinationSpotId) {
      queryClient.invalidateQueries({
        queryKey: yardQueryKeys.spotDetail(event.child.destinationSpotId)
      });
    }
  };

  const handleUpdateYardTaskEvent = (event: YardTaskUpdateEvent) => {
    if (event?.id === effectiveTaskId.value) {
      if (event?.type === 'move') {
        invalidateTasksQueries(event);
      }
      if (isInViewport.value) {
        invalidate();
      } else {
        pendingInvalidation.value = true;
      }
    }
  };

  /**
   * Intersection handler so callers can control whether the task is considered "in view".
   * When the task comes back into view and there is a pending invalidation, a refresh is triggered.
   */
  const handleIntersect = async (entry?: { isIntersecting?: boolean } | boolean) => {
    const isIntersecting = typeof entry === 'boolean' ? entry : entry?.isIntersecting ?? true;
    isInViewport.value = isIntersecting;

    if (isIntersecting && pendingInvalidation.value) {
      pendingInvalidation.value = false;
      invalidate();
    }
  };

  watch(task, newTask => {
    if (onUpdateReceived && newTask) {
      onUpdateReceived(newTask);
    }
  });

  watch(effectiveTaskId, (newTaskId, oldTaskId) => {
    if (newTaskId !== oldTaskId) {
      pendingInvalidation.value = false;
    }
  });

  onMounted(async () => {
    if (live) {
      eventHub.on('update-YardTask', handleUpdateYardTaskEvent);
    }
  });

  onBeforeUnmount(() => {
    if (live) {
      eventHub.off('update-YardTask', handleUpdateYardTaskEvent);
    }
  });

  return {
    rawTask,
    task,
    warehouseTimezone,

    isLoading,
    isFetching,
    error,

    refresh,
    invalidate,

    onIntersect: handleIntersect
  };
}
